import { ApiEndpoints } from '@lib/enums/ApiEndpoints';
import { ModelInformationDict } from '@lib/enums/ModelInformation';
import type { ModelType } from '@lib/enums/ModelType';
import { apiUrl } from '@lib/functions/Api';
import { getDetailUrl } from '@lib/functions/Navigation';
import { t } from '@lingui/core/macro';
import { showNotification } from '@mantine/notifications';
import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApi } from '../contexts/ApiContext';
import { extractErrorMessage } from '../functions/api';
import { useUserState } from '../states/UserState';

const BARCODE_TRIGGER_KEY = '^';

export function useGlobalBarcodeScan() {
  const api = useApi();
  const navigate = useNavigate();
  const user = useUserState();
  const bufferRef = useRef('');
  const isCapturingRef = useRef(false);

  const defaultScan = useCallback(
    (data: Record<string, any>) => {
      let match = false;

      for (const model_type of Object.keys(ModelInformationDict)) {
        if (data[model_type]?.['pk']) {
          if (user.hasViewPermission(model_type as ModelType)) {
            const url = getDetailUrl(
              model_type as ModelType,
              data[model_type]['pk']
            );
            navigate(url);
            match = true;
            break;
          }
        }
      }

      if (!match) {
        showNotification({
          title: t`No matching item found`,
          message: t`No matching item found`,
          color: 'red'
        });
      }
    },
    [navigate, user]
  );

  const handleBarcode = useCallback(
    (barcode: string) => {
      if (!barcode || barcode.length === 0) {
        return;
      }

      api
        .post(apiUrl(ApiEndpoints.barcode), {
          barcode: barcode
        })
        .then((response: any) => {
          const data = response.data ?? {};
          defaultScan(data);
        })
        .catch((error) => {
          const message = extractErrorMessage({
            error: error,
            field: 'error',
            defaultMessage: t`Failed to scan barcode`
          });

          showNotification({
            title: t`Failed to scan barcode`,
            message: message,
            color: 'red'
          });
        });
    },
    [api, defaultScan]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) {
        return;
      }

      if (event.key === BARCODE_TRIGGER_KEY) {
        event.preventDefault();
        event.stopPropagation();
        isCapturingRef.current = true;
        bufferRef.current = '';
        return;
      }

      if (!isCapturingRef.current) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.key === 'Enter') {
        const barcode = bufferRef.current.trim();
        bufferRef.current = '';
        isCapturingRef.current = false;
        handleBarcode(barcode);
        return;
      }

      if (event.key === 'Escape') {
        bufferRef.current = '';
        isCapturingRef.current = false;
        return;
      }

      if (event.key === 'Backspace') {
        bufferRef.current = bufferRef.current.slice(0, -1);
        return;
      }

      if (event.key.length === 1) {
        bufferRef.current += event.key;
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [handleBarcode]);
}
